import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { throttle } from 'lodash';
import { Helmet } from 'react-helmet';
import './App.css';

function App() {
  const mountRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const touchStartRef = useRef({ x: 0, y: 0 });

  // Create refs to store Three.js instances
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const modelRef = useRef<THREE.Group | null>(null);
  const initialRotationRef = useRef<number>(0);
  // Track if we're currently loading a model to prevent duplicate load attempts
  const isLoadingModelRef = useRef<boolean>(false);

  // Three.js setup and model loading
  useEffect(() => {
    if (!mountRef.current) return;

    // Only create scene, camera, and renderer if they don't exist
    if (!sceneRef.current) {
      sceneRef.current = new THREE.Scene();
      sceneRef.current.background = new THREE.Color(0x000000);
    }

    if (!cameraRef.current) {
      cameraRef.current = new THREE.PerspectiveCamera(
        isMobile ? 60 : 50,
        window.innerWidth / window.innerHeight,
        0.1,
        1000
      );
      // Adjust camera position for mobile
      const [x, y, z] = isMobile ? [2.5, 2.5, 6] : [2, 2, 5];
      cameraRef.current.position.set(x, y, z);
      cameraRef.current.lookAt(0, 0, 0);
    }

    if (!rendererRef.current) {
      rendererRef.current = new THREE.WebGLRenderer({
        antialias: !isMobile, // Disable antialiasing on mobile
        powerPreference: "high-performance",
        stencil: false,
        depth: true
      });

      rendererRef.current.shadowMap.enabled = !isMobile;
      rendererRef.current.shadowMap.type = THREE.PCFSoftShadowMap;
      rendererRef.current.setSize(window.innerWidth, window.innerHeight);
      rendererRef.current.setPixelRatio(isMobile ? 1 : Math.min(window.devicePixelRatio, 2));
      rendererRef.current.autoClear = true;
      rendererRef.current.setClearColor(0x000000, 1);
      mountRef.current.appendChild(rendererRef.current.domElement);
    }

    const scene = sceneRef.current;
    const camera = cameraRef.current;
    const renderer = rendererRef.current;

    // Add lights only once
    if (scene.children.filter(child => child instanceof THREE.Light).length === 0) {
      // Ambient light for overall scene brightness
      const ambientLight = new THREE.AmbientLight(0xffffff, 1);
      scene.add(ambientLight);

      // Main directional lights
      const directionalLight = new THREE.DirectionalLight(0xffffff, 4);
      directionalLight.position.set(6, 6, 6);
      directionalLight.castShadow = true;
      scene.add(directionalLight);

      const additionalLight = new THREE.DirectionalLight(0xffffff, 3);
      additionalLight.position.set(-6, 6, 6);
      additionalLight.castShadow = true;
      scene.add(additionalLight);

      // Add spotlights for focused illumination
      const spotLight1 = new THREE.SpotLight(0xffffff, 8);
      spotLight1.position.set(0, 5, 0);
      spotLight1.angle = Math.PI / 4;
      spotLight1.penumbra = 0.3;
      spotLight1.decay = 1;
      spotLight1.distance = 20;
      spotLight1.castShadow = true;
      scene.add(spotLight1);

      const spotLight2 = new THREE.SpotLight(0xffffff, 6);
      spotLight2.position.set(5, 3, 5);
      spotLight2.angle = Math.PI / 6;
      spotLight2.penumbra = 0.2;
      spotLight2.decay = 1;
      spotLight2.distance = 15;
      spotLight2.castShadow = true;
      scene.add(spotLight2);

      const spotLight3 = new THREE.SpotLight(0xffffff, 6);
      spotLight3.position.set(-5, 3, -5);
      spotLight3.angle = Math.PI / 6;
      spotLight3.penumbra = 0.2;
      spotLight3.decay = 1;
      spotLight3.distance = 15;
      spotLight3.castShadow = true;
      scene.add(spotLight3);
    }

    // Clean up any existing models first
    const existingModels = scene.children.filter(child =>
      child instanceof THREE.Group && child !== modelRef.current
    );
    existingModels.forEach(model => {
      scene.remove(model);
    });

    // Load model only if it's not already loaded and we're not already loading
    if (!modelRef.current && !isLoadingModelRef.current) {
      isLoadingModelRef.current = true;

      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
      dracoLoader.preload();

      const loader = new GLTFLoader();
      loader.setDRACOLoader(dracoLoader);

      console.log('Starting model load attempt...');
      const modelPath = `${window.location.origin}/model/scene.gltf`;
      console.log('Loading model from:', modelPath);
      loader.load(
        modelPath,
        (gltf) => {
          console.log('GLTF loaded:', gltf);
          console.log('Scene children count:', gltf.scene.children.length);
          const model = gltf.scene;

          model.traverse((node) => {
            if (node instanceof THREE.Mesh) {
              if (node.material) {
                node.material.precision = "lowp";
                node.material.fog = false;
                node.material.flatShading = true;
              }
              if (node.geometry) {
                node.geometry.computeBoundingSphere();
                node.geometry.computeBoundingBox();
              }
            }
          });

          const box = new THREE.Box3().setFromObject(model);
          const center = box.getCenter(new THREE.Vector3());
          const size = box.getSize(new THREE.Vector3());

          const maxDim = Math.max(size.x, size.y, size.z);
          const scale = 2 / maxDim;
          model.scale.multiplyScalar(scale);
          model.position.sub(center.multiplyScalar(scale));

          // Add 45-degree initial rotation (Math.PI / 4 radians = 45 degrees)
          model.rotation.y = Math.PI / 7;

          // Store the initial rotation for rubber band effect
          initialRotationRef.current = model.rotation.y;

          // Store reference to model
          modelRef.current = model;

          // Add to scene AFTER storing reference
          scene.add(model);

          isLoadingModelRef.current = false;
          setLoadingProgress(100);
        },
        (progress) => {
          if (progress.total > 0) {
            const percent = (progress.loaded / progress.total) * 100;
            console.log('Loading progress:', percent + '%');
            setLoadingProgress(percent);
          }
        },
        (error) => {
          console.error('Error loading model:', error);
          isLoadingModelRef.current = false;
          setLoadingProgress(0);
        }
      );
    }

    // Throttle scroll handler - implement rubber band effect
    const handleScroll = throttle(() => {
      if (!containerRef.current || !modelRef.current) return;

      const scrollPercent = containerRef.current.scrollTop /
        (containerRef.current.scrollHeight - containerRef.current.clientHeight);

      if (modelRef.current) {
        // Rubber band effect - rotation increases with scroll and returns to original position
        const maxRotation = Math.PI * 2; // Maximum rotation at bottom of page
        const targetRotation = initialRotationRef.current + (scrollPercent * maxRotation);

        // Smoother transition with easing
        modelRef.current.rotation.y += (targetRotation - modelRef.current.rotation.y) * 0.1;

        // Zoom effect with rubber band behavior
        const startZoom = 3;
        const endZoom = 1;
        const targetZoom = startZoom - (scrollPercent * (startZoom - endZoom));
        const currentZoom = camera.position.length();
        const newZoom = currentZoom + (targetZoom - currentZoom) * 0.1;

        camera.position.setLength(newZoom);
        camera.lookAt(0, 0, 0);

        // Force a render after changing model and camera
        renderer.render(scene, camera);
      }
    }, 16); // More responsive throttle for smoother animation

    let lastTime = 0;
    const targetFPS = 30;
    const frameInterval = 1000 / targetFPS;

    const animate = (currentTime: number) => {
      const deltaTime = currentTime - lastTime;

      if (deltaTime > frameInterval) {
        lastTime = currentTime - (deltaTime % frameInterval);
        renderer.render(scene, camera);
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    let animationFrameId = requestAnimationFrame(animate);

    const handleResize = throttle(() => {
      if (!containerRef.current) return;

      if (camera && renderer) {
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      }
    }, 250);

    window.addEventListener('resize', handleResize);

    // Attach the scroll listener directly to containerRef.current
    if (containerRef.current) {
      containerRef.current.addEventListener('scroll', handleScroll);
      console.log('Scroll event listener attached to container');
    }

    return () => {
      cancelAnimationFrame(animationFrameId);
      if (containerRef.current) {
        containerRef.current.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobile]);

  // Scroll tracking
  useEffect(() => {
    const handleNavScroll = () => {
      if (containerRef.current) {
        setIsScrolled(containerRef.current.scrollTop > 50);
        setShowBackToTop(containerRef.current.scrollTop > 300);
      }
    };

    if (containerRef.current) {
      containerRef.current.addEventListener('scroll', handleNavScroll);
    }

    return () => {
      if (containerRef.current) {
        containerRef.current.removeEventListener('scroll', handleNavScroll);
      }
    };
  }, []);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Touch event handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY
    };
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!modelRef.current || !touchStartRef.current) return;

    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;

    // Smooth rotation based on touch movement
    modelRef.current.rotation.y += deltaX * 0.005;

    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY
    };
  }, []);

  // Navigation and scroll helpers
  const handleBackToTop = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, []);

  const handleNavClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const href = e.currentTarget.getAttribute('href');
    if (!href || !containerRef.current) return;

    if (href === '#') {
      containerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }

    const targetElement = document.querySelector(href);
    if (targetElement && containerRef.current) {
      const topOffset = targetElement.getBoundingClientRect().top + containerRef.current.scrollTop;
      containerRef.current.scrollTo({
        top: topOffset,
        behavior: 'smooth'
      });
    }
  }, []);

  return (
    <div className="app-container" ref={containerRef}>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Bilhjälpen Arlanda | Professionell Bilservice & Reparation</title>
        <meta name="description" content="Auktoriserad bilverkstad i Arlanda. Expert på bilservice, reparationer, plåt och lack." />
        <meta name="keywords" content="bilverkstad arlanda, plåt, lack, bilservice, bilreparation" />
        <meta property="og:title" content="Bilhjälpen Arlanda | Professionell Bilservice" />
        <meta property="og:description" content="Specialister på plåt och lack. Skräddarsydda lösningar för varje kunds behov." />
        <meta name="theme-color" content="#0a0a14" />
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5, minimum-scale=1" />
      </Helmet>

      <header className={`nav-header ${isScrolled ? 'scrolled' : ''}`}>
        <a href="#" className="nav-logo" onClick={handleNavClick}>
          <img 
            src={`${process.env.PUBLIC_URL}/images/LOGGA.svg`} 
            alt="Bilhjälpen Arlanda Logo" 
            className="header-logo"
            width="auto"
            height="70" 
          />
        </a>
        
        {/* Mobile Menu Toggle Button */}
        <button 
          className={`mobile-menu-toggle ${mobileMenuOpen ? 'open' : ''}`} 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle navigation menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
        
        {/* Desktop navigation */}
        <nav className={`nav-links ${isMobile ? 'mobile' : ''} ${mobileMenuOpen ? 'open' : ''}`}>
          <a href="#tjanster" className="nav-link" onClick={(e) => {
            handleNavClick(e);
            if (isMobile) setMobileMenuOpen(false);
          }}>Tjänster</a>
          <a href="#process" className="nav-link" onClick={(e) => {
            handleNavClick(e);
            if (isMobile) setMobileMenuOpen(false);
          }}>Process</a>
          <a href="#om-oss" className="nav-link" onClick={(e) => {
            handleNavClick(e);
            if (isMobile) setMobileMenuOpen(false);
          }}>Om Oss</a>
          <a href="#boka" className="nav-link" onClick={(e) => {
            handleNavClick(e);
            if (isMobile) setMobileMenuOpen(false);
          }}>Kontakt</a>
          <a href="tel:0737538807" className="nav-link nav-phone">Ring Oss</a>
        </nav>
      </header>

      {/* 3D Car Model Container */}
      <div
        className="webgl-container"
        ref={mountRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
      />
      
      {/* Loading Indicator */}
      {loadingProgress < 100 && (
        <div className="loading-overlay">
          <div className="loading-progress">
            <div className="loading-bar" style={{ width: `${loadingProgress}%` }} />
            <div className="loading-text">Laddar 3D-modell... {Math.round(loadingProgress)}%</div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="content-section section-intro">
        <h1>Bilhjälpen Arlanda</h1>
        <p className="hero-text">
          Vi specialiserar oss inom plåt och lack,
          vilket innebär att vi erbjuder skräddarsydda lösningar för varje kunds behov.
          Med vår expertis och moderna utrustning kan vi hantera allt från avancerade plåtarbeten till professionell lackering.
        </p>
        <div>
          <a href="#boka" className="cta-button" onClick={handleNavClick}>Kontakta Oss</a>
          <a href="#tjanster" className="cta-button" onClick={handleNavClick}>Våra Tjänster</a>
        </div>
      </section>

      {/* Services Section */}
      <section id="tjanster" className="content-section section-services">
        <h2>Våra Specialisttjänster</h2>
        <div className="services-grid">
          <div className="service-item">
            <div className="service-icon">🔧</div>
            <h3>Plåt och Lack</h3>
            <p>Vi erbjuder skräddarsydda lösningar för varje kunds behov med fokus på högsta kvalitet och precision i alla plåt- och lackeringsarbeten.</p>
          </div>
          <div className="service-item">
            <div className="service-icon">🚗</div>
            <h3>Glasbyte</h3>
            <p>Snabba och professionella glasbyten på alla typer av fordon för att säkerställa säkerheten och kvaliteten på ditt fordon.</p>
          </div>
          <div className="service-item">
            <div className="service-icon">✨</div>
            <h3>Exklusiv Styling</h3>
            <p>Skräddarsydd exteriör styling med noggrant anpassade designelement som fälgar, spoilrar och andra stilistiska inslag.</p>
          </div>
          <div className="service-item">
            <div className="service-icon">⚙️</div>
            <h3>Modern Utrustning</h3>
            <p>Toppmodern utrustning och senaste tekniken för att leverera resultat som överträffar förväntningar med högsta precision.</p>
          </div>
        </div>
        <div className="price-info">
          <h3>Samarbete med Försäkringsbolag</h3>
          <p>Vi samarbetar med flera försäkringsbolag och hanterar all kommunikation och administration, vilket gör processen smidig för dig som kund.</p>
          <a href="#boka" className="cta-button" onClick={handleNavClick}>Kontakta Oss</a>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="content-section section-process">
        <h2>Vår Arbetsprocess</h2>
        <div className="process-steps">
          <div className="step">
            <div className="step-number">1</div>
            <h3>Skadeanmälan</h3>
            <p>Vi hjälper dig med att anmäla skadan och genomföra en grundlig besiktning.</p>
          </div>
          <div className="step">
            <div className="step-number">2</div>
            <h3>Skicka Kalkyl</h3>
            <p>Vi skickar en detaljerad kalkyl till ditt försäkringsbolag för godkännande.</p>
          </div>
          <div className="step">
            <div className="step-number">3</div>
            <h3>Påbörja Reparation</h3>
            <p>Efter godkännande startar vi reparationsarbetet med högsta precision.</p>
          </div>
          <div className="step">
            <div className="step-number">4</div>
            <h3>Kvalitetskontroll</h3>
            <p>Noggrann kontroll av alla utförda arbeten innan återlämning av ditt fordon.</p>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="om-oss" className="content-section section-about">
        <h2>Varför Välja Oss?</h2>
        <div className="features-grid">
          <div className="feature-item">
            <div className="feature-icon">✓</div>
            <h3>Certifierade Specialister</h3>
            <p>Våra tekniker är certifierade specialister inom plåt och lack, med omfattande kunskap och erfarenhet.</p>
          </div>
          <div className="feature-item">
            <div className="feature-icon">⭐</div>
            <h3>Transparent Prissättning</h3>
            <p>Inga överraskningar - vi ger alltid en detaljerad offert innan arbetet påbörjas.</p>
          </div>
          <div className="feature-item">
            <div className="feature-icon">🏆</div>
            <h3>Modern Teknik</h3>
            <p>Vi investerar kontinuerligt i den senaste tekniken för att säkerställa högsta kvalitet på alla våra arbeten.</p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="boka" className="content-section section-contact">
        <h2>Kontakta Oss</h2>
        <div className="contact-grid">
          <div className="contact-info">
            <h3>Hitta Oss</h3>
            <p>
              Besöksadress: Thulins Plats 8G<br />
              195 61 Arlandastad<br />
              <strong>Telefon:</strong> 0737538807<br />
              <strong>E-post:</strong> info@bilhjalpenarlanda.se
            </p>
            <h3>Öppettider</h3>
            <p>
              Måndag - Fredag: 08:00 - 17:00<br />
              Lördag: Stängt<br />
              Söndag: Stängt
            </p>
          </div>
          <div className="contact-info">
            <h3>Försäkringsärenden</h3>
            <p>Vi hjälper dig med hela processen för ditt försäkringsärende, från skadeanmälan till färdig reparation.</p>
            <div className="social-links">
              <a href="https://instagram.com/bilhjalpenarlanda" className="social-link">Instagram</a>
            </div>
          </div>
        </div>
      </section>

      {/* Back to top button */}
      <a
        href="#"
        className={`back-to-top ${showBackToTop ? 'visible' : ''}`}
        onClick={handleBackToTop}
        aria-label="Tillbaka till toppen"
      >
        ↑
      </a>
    </div>
  );
}

export default App;
